import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ModuleKey } from '../types';
import logo from '../assets/logo.png';

// Sidebar auto-collapses below this width (tablets & phones); it pushes content
// on desktop but overlays it on smaller screens so panels get the full width.
const DESKTOP_MIN_WIDTH = 1024; // Tailwind `lg`

interface NavItem {
  name: string;
  path: string;
  icon: string;
  module?: ModuleKey;
}

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Start open on desktop, collapsed on tablet/phone. On small screens the
  // sidebar overlays the content instead of pushing it, so panel pages get room.
  const isDesktop = () =>
    typeof window !== 'undefined' && window.innerWidth >= DESKTOP_MIN_WIDTH;
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const [overlay, setOverlay] = useState(() => !isDesktop());

  // Keep the mode in sync when the viewport crosses the desktop threshold
  // (e.g. a tablet rotating landscape↔portrait, or a window resize).
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= DESKTOP_MIN_WIDTH;
      setOverlay(!desktop);
      setSidebarOpen(desktop);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // On tablet/phone, tapping a nav link should close the overlay so the user
  // lands on the page with the full width available.
  const handleNavClick = () => {
    if (overlay) setSidebarOpen(false);
  };

  const navigation: NavItem[] = [
    { name: 'Dashboard',       path: '/',           icon: '📊' },
    { name: 'Masters',         path: '/masters',    icon: '📚', module: 'masters' },
    { name: 'Procurement',     path: '/procurement',icon: '🛒', module: 'procurement' },
    { name: 'Inbound',         path: '/inbound',    icon: '📦', module: 'inbound' },
    { name: 'Storage',         path: '/storage',    icon: '🗄️', module: 'storage' },
    { name: 'Customer Orders', path: '/orders',     icon: '📋', module: 'customer_orders' },
    { name: 'Mapping',         path: '/mapping-scheduling', icon: '📌', module: 'mapping_scheduling' },
    { name: 'Production',      path: '/production',  icon: '⚙️' },
    { name: 'Packs',           path: '/packs',      icon: '📦', module: 'packs' },
    { name: 'Loading',         path: '/loading',    icon: '🏗️', module: 'loading' },
    { name: 'Delivery',        path: '/delivery',   icon: '🚚', module: 'delivery' },
    { name: 'Audit Logs',      path: '/audit-logs', icon: '📜', module: 'audit_logs' },
    { name: 'Data Tools',      path: '/data-tools', icon: '🛠️', module: 'data_tools' },
  ];

  const filteredNavigation = navigation.filter(
    (item) => !item.module || (user?.module_permissions?.[item.module] ?? 'NONE') !== 'NONE'
  );

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200/60 fixed top-0 left-0 right-0 z-10">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 gap-2">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-600 hover:text-gray-900 text-xl leading-none flex-shrink-0"
              aria-label="Toggle navigation"
            >
              ☰
            </button>
            <img src={logo} alt="SARO Steel" className="h-9 sm:h-10 object-contain flex-shrink-0" />
          </div>
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            {user && (
              <span className="hidden sm:inline text-sm text-gray-600 truncate">
                {user.first_name || user.username} ({user.role})
              </span>
            )}
            <Link
              to="/settings"
              className="text-gray-500 hover:text-gray-700 transition-colors"
              title="Settings"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </Link>
            <button
              onClick={handleLogout}
              className="text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Backdrop — only in overlay (tablet/phone) mode when the sidebar is open */}
      {overlay && sidebarOpen && (
        <div
          className="fixed inset-0 top-16 bg-black/30 z-20 transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 bottom-0 bg-gray-50/95 backdrop-blur-sm border-r border-gray-200/60 transition-all duration-300 ease-out ${sidebarOpen ? 'w-64' : 'w-0'
          } overflow-hidden z-30`}
      >
        <nav className="p-4 space-y-1">
          {filteredNavigation.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${isActive(item.path)
                ? 'bg-primary-50 text-primary-700 font-medium shadow-soft'
                : 'text-gray-600 hover:bg-gray-100/80'
                }`}
            >
              <span className="text-xl">{item.icon}</span>
              <span>{item.name}</span>
            </Link>
          ))}
        </nav>
      </aside>

      {/* Main Content — pushed by the sidebar only on desktop; overlaid on tablet/phone */}
      <main
        className={`pt-16 transition-all duration-300 ${sidebarOpen && !overlay ? 'ml-64' : 'ml-0'
          }`}
      >
        <div className="p-4 sm:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
